// External
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import _ from "lodash";
import { useTranslation } from "react-i18next";

// Internal
import { useDispatch } from "Hooks/useDispatch";
import { setToastMsg } from "Login/modules/LoginModule";
import { updateTranscriptGradeScalesMutationHandler } from "../../../../../module/AcademicRecordsThunks";

// Relative
import config from "../../../modules/config.json";
import {
  TAB_KEYS,
  GRADE_SCALE_VIEW_TYPES,
  GRADING_PERIOD_COLUMNS_SECTION_ID,
} from "../../../modules/constants";
import { getTermWiseColumnSections } from "../../../modules/utils";
import { useTranscriptData } from "./useTranscriptData";
import { useColumnViewModel } from "./useColumnViewModel";
import { useRowViewModel } from "./useRowViewModel";
import { useListItemsState } from "./useListItemsState";
import { useListItemHandlers } from "./useListItemHandlers";
export const useDefaultSettingsContentData = ({
  academicRecordsData,
  orgGradeScalesData,
  curriculumProgramsData,
  selectedGradeId,
  onUpdatesChange,
  onRefetchOrgGradeScales,
  draftConfiguration,
  initialListItems,
  initialIsDirty,
  isAcrossYearAggregatesMode = false,
  onListItemsChange,
  gradingPeriodsTabProps,
  effectiveGradingPeriods,
  defaultGradeScalesConfig,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation(["common", "transcript"]);
  const dispatch = useDispatch();
  const [selectedTab, setSelectedTab] = useState(() => {
    if (isAcrossYearAggregatesMode) return TAB_KEYS.ROWS;
    if (selectedGradeId) return TAB_KEYS.GRADING_PERIODS;
    return TAB_KEYS.COLUMNS;
  });
  const [isConfigureColumnsOpen, setIsConfigureColumnsOpen] = useState(false);
  const [isUpdatingGradeScales, setIsUpdatingGradeScales] = useState(false);
  const scrollableContentRef = useRef(null);
  const shouldShowGradeScaleViewToggle =
    Boolean(selectedGradeId) && !isAcrossYearAggregatesMode;

  const tabs = useMemo(() => {
    const baseTabs = [
      {
        value: TAB_KEYS.COLUMNS,
        label: t("common:column_plural"),
      },
      {
        value: TAB_KEYS.ROWS,
        label: t("common:row_other"),
      },
    ];
    if (selectedGradeId && !isAcrossYearAggregatesMode) {
      return [
        {
          value: TAB_KEYS.GRADING_PERIODS,
          label: t("common:grading_period_plural"),
        },
        ...baseTabs,
      ];
    }
    return baseTabs;
  }, [t, selectedGradeId, isAcrossYearAggregatesMode]);

  const configuredSections = useMemo(() => {
    if (isAcrossYearAggregatesMode) {
      return [];
    }
    const configPath = selectedGradeId
      ? "displaySettingsItems.gradeLevel.columns.sections"
      : "displaySettingsItems.default.columns.sections";
    return _.get(config, configPath, []);
  }, [isAcrossYearAggregatesMode, selectedGradeId]);

  const configuredRowSections = useMemo(() => {
    if (isAcrossYearAggregatesMode) {
      return _.get(
        config,
        "displaySettingsItems.acrossYearAggregates.rows.sections",
        []
      );
    }
    const configPath = selectedGradeId
      ? "displaySettingsItems.gradeLevel.rows.sections"
      : "displaySettingsItems.default.rows.sections";
    return _.get(config, configPath, []);
  }, [isAcrossYearAggregatesMode, selectedGradeId]);

  // Use data processing hook
  const {
    selectedGradeMap,
    originalConfiguration,
    transcriptConfiguration,
    gradeScaleDescriptionMap,
    manageColumnItemIdSet,
    transcriptToSourceIdMap,
    gradingPeriods,
  } = useTranscriptData({
    academicRecordsData,
    orgGradeScalesData,
    curriculumProgramsData,
    selectedGradeId,
    draftConfiguration,
    effectiveGradingPeriods,
    t,
  });
  const initialSelectedGradeScaleView = useMemo(() => {
    const savedViewType = _.get(
      transcriptConfiguration,
      "gradeScales.viewType",
      GRADE_SCALE_VIEW_TYPES.GRADING_PERIOD_WISE
    );

    return _.includes(_.values(GRADE_SCALE_VIEW_TYPES), savedViewType)
      ? savedViewType
      : GRADE_SCALE_VIEW_TYPES.GRADING_PERIOD_WISE;
  }, [transcriptConfiguration]);
  const [selectedGradeScaleView, setSelectedGradeScaleView] = useState(
    initialSelectedGradeScaleView
  );

  const {
    sectionViewModel: gradingPeriodWiseSectionViewModel,
  } = useColumnViewModel({
    configuredSections,
    transcriptConfiguration,
    selectedGradeMap,
    gradingPeriods,
    selectedGradeScaleView: GRADE_SCALE_VIEW_TYPES.GRADING_PERIOD_WISE,
    gradeScaleDescriptionMap,
    manageColumnItemIdSet,
    defaultGradeScalesConfig,
    orgGradeScalesData,
    curriculumProgramsData,
    language,
    t,
  });

  const { sectionViewModel: acrossSectionViewModel } = useColumnViewModel({
    configuredSections,
    transcriptConfiguration,
    selectedGradeMap,
    gradingPeriods,
    selectedGradeScaleView: GRADE_SCALE_VIEW_TYPES.ACROSS_GRADING_PERIODS,
    gradeScaleDescriptionMap,
    manageColumnItemIdSet,
    defaultGradeScalesConfig,
    orgGradeScalesData,
    curriculumProgramsData,
    language,
    t,
  });

  // When all grading periods are unchecked, force "Across all" view —
  // the "For each grading period" section has nothing to show.
  // Also hide the radio toggle since there's nothing to switch between.
  const hasNoEnabledGradingPeriods =
    Boolean(selectedGradeId) && _.isEmpty(gradingPeriods);

  const sectionViewModel = useMemo(
    () =>
      selectedGradeScaleView ===
        GRADE_SCALE_VIEW_TYPES.ACROSS_GRADING_PERIODS ||
      hasNoEnabledGradingPeriods
        ? acrossSectionViewModel
        : gradingPeriodWiseSectionViewModel,
    [
      acrossSectionViewModel,
      gradingPeriodWiseSectionViewModel,
      hasNoEnabledGradingPeriods,
      selectedGradeScaleView,
    ]
  );

  // Use row view model hook
  const { rowSectionViewModel } = useRowViewModel({
    configuredRowSections,
    transcriptConfiguration,
    language,
    t,
  });

  // When all GPs are unchecked, treat as across view for config building
  // so toggle changes are saved to acrossGradingPeriods config (not lost)
  const effectiveGradeScaleView = hasNoEnabledGradingPeriods
    ? GRADE_SCALE_VIEW_TYPES.ACROSS_GRADING_PERIODS
    : selectedGradeScaleView;

  // Use list items state hook
  const { listItems, updateList, markAsDirty } = useListItemsState({
    sectionViewModel,
    rowSectionViewModel,
    gradingPeriods,
    originalConfiguration,
    baseConfiguration: transcriptConfiguration,
    selectedGradeScaleView: effectiveGradeScaleView,
    initialListItems,
    initialIsDirty,
    selectedGradeId,
    onUpdatesChange,
    onListItemsChange,
  });

  const handleGradeScaleViewChange = useCallback(
    nextView => {
      if (nextView === selectedGradeScaleView) {
        return;
      }

      setSelectedGradeScaleView(nextView);
      markAsDirty();

      if (!shouldShowGradeScaleViewToggle) {
        return;
      }

      const targetSectionViewModel =
        nextView === GRADE_SCALE_VIEW_TYPES.ACROSS_GRADING_PERIODS
          ? acrossSectionViewModel
          : gradingPeriodWiseSectionViewModel;
      const gradeScaleSection = _.find(targetSectionViewModel, {
        sectionId: GRADING_PERIOD_COLUMNS_SECTION_ID,
      });
      const gradeScaleItems = _.get(gradeScaleSection, "items", []);

      if (_.isEmpty(gradeScaleItems)) {
        return;
      }

      updateList({
        listKey: GRADING_PERIOD_COLUMNS_SECTION_ID,
        updater: () => gradeScaleItems,
        markDirty: false,
      });
    },
    [
      acrossSectionViewModel,
      gradingPeriodWiseSectionViewModel,
      markAsDirty,
      selectedGradeScaleView,
      shouldShowGradeScaleViewToggle,
      updateList,
    ]
  );

  const gpSectionItemIds = useMemo(() => {
    const gpSection = _.find(sectionViewModel, {
      sectionId: GRADING_PERIOD_COLUMNS_SECTION_ID,
    });
    return _.map(_.get(gpSection, "items", []), "id").join(",");
  }, [sectionViewModel]);

  // Initialize from the actual listItems (cached or built on mount)
  // so the sync only fires when GPs actually change, not on every mount
  const [prevGpItemIds, setPrevGpItemIds] = useState(() => {
    const cachedItems = _.get(listItems, GRADING_PERIOD_COLUMNS_SECTION_ID);
    return cachedItems
      ? _.map(cachedItems, "id").join(",")
      : gpSectionItemIds;
  });
  if (gpSectionItemIds !== prevGpItemIds) {
    setPrevGpItemIds(gpSectionItemIds);
    const gpSection = _.find(sectionViewModel, {
      sectionId: GRADING_PERIOD_COLUMNS_SECTION_ID,
    });
    const freshItems = _.get(gpSection, "items", []);
    updateList({
      listKey: GRADING_PERIOD_COLUMNS_SECTION_ID,
      updater: () => freshItems,
      markDirty: false,
    });
  }

  // Use handlers hook
  const {
    handleToggleItem,
    handleLabelUpdate,
    handleReorder,
    handleReorderForGradingPeriod,
    handleChildToggle,
    handleDeleteManageColumnItem,
    updateManageColumnsSelection,
    getEnabledKeys,
  } = useListItemHandlers({
    updateList,
    gradingPeriods,
    gradeScaleDescriptionMap,
    transcriptToSourceIdMap,
    language,
  });

  const handleSaveCompleted = useCallback(() => {
    setIsConfigureColumnsOpen(false);
  }, []);

  const handleSaveGradeScales = useCallback(
    input => {
      setIsUpdatingGradeScales(true);
      dispatch(
        updateTranscriptGradeScalesMutationHandler({
          input,
          onCompleted: () => handleSaveCompleted(input),
          onFinally: () => setIsUpdatingGradeScales(false),
        })
      );
    },
    [dispatch, handleSaveCompleted]
  );

  const columnSectionsToRender = useMemo(
    () =>
      _.flatMap(sectionViewModel, section => {
        const storedItems = listItems[section.sectionId];
        // For the GP columns section, detect stale listItems by comparing
        // item IDs. When grading periods change, the stored items have
        // different IDs than the fresh section items — use fresh items
        // to avoid showing stale/missing columns.
        const sectionItems = storedItems || section.items;

        return getTermWiseColumnSections({
          section,
          sectionItems,
          gradingPeriods,
        });
      }),
    [sectionViewModel, listItems, gradingPeriods]
  );

  useEffect(() => {
    if (scrollableContentRef.current) {
      scrollableContentRef.current.scrollTop = 0;
    }
  }, [selectedTab]);

  const getColumnSectionHandlers = useCallback(
    section => {
      const listKey = section.listKey;
      const gradingPeriodPrefix = section.gradingPeriodPrefix;

      return {
        onReorder: updatedItems =>
          handleReorderForGradingPeriod({
            listKey,
            updatedItems,
            gradingPeriodPrefix,
          }),
        onToggle: id => handleToggleItem({ listKey, id }),
        onLabelUpdate: (id, content) =>
          handleLabelUpdate({ listKey, id, content }),
        onDelete: id => handleDeleteManageColumnItem({ listKey, id }),
        onSelectionChange: ({ selectedKeys, keyToOptionMap }) =>
          updateManageColumnsSelection({
            listKey,
            gradingPeriodPrefix,
            selectedKeys,
            keyToOptionMap,
          }),
        onSyncStart: () => setIsUpdatingGradeScales(true),
        onSyncEnd: () => setIsUpdatingGradeScales(false),
        onSyncError: () => {
          dispatch(setToastMsg("toastMsgs:something_went_wrong"));
        },
        onOpenSettingsClick: () => setIsConfigureColumnsOpen(true),
      };
    },
    [
      dispatch,
      handleDeleteManageColumnItem,
      handleLabelUpdate,
      handleReorderForGradingPeriod,
      handleToggleItem,
      updateManageColumnsSelection,
    ]
  );

  return {
    academicRecordsData,
    columnSectionsToRender,
    curriculumProgramsData,
    getColumnSectionHandlers,
    getEnabledKeys,
    handleChildToggle,
    handleLabelUpdate,
    handleReorder,
    handleSaveGradeScales,
    handleToggleItem,
    isAcrossYearAggregatesMode,
    isConfigureColumnsOpen,
    isUpdatingGradeScales,
    listItems,
    onRefetchOrgGradeScales,
    orgGradeScalesData,
    scrollableContentRef,
    selectedGradeId,
    selectedTab,
    rowSectionViewModel,
    selectedGradeScaleView,
    handleGradeScaleViewChange,
    hasNoEnabledGradingPeriods,
    shouldShowGradeScaleViewToggle,
    setIsConfigureColumnsOpen,
    setSelectedTab,
    t,
    tabs,
    updateList,
    gradingPeriodsTabProps,
  };
};
